// REACT
import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

// MATERIAL UI
import { FormControl, Grid, Typography, Box, LinearProgress } from "@mui/material";

// COMPONENTS
import { FormSelect, SelectType } from "../Modal/Withdraw/FormSelect";
import { FormInput, InputStyleType } from "../../input/FormInput";
import { StyledLink } from "../Modal/Withdraw/Link";
import { StyledError } from "../Modal/Withdraw/Error";
import { AlertCard } from "src/components/alerts/AlertCard";

// WITHDRAWAL STATE
import {
  setToken,
  setWalletAddress,
  setMemo,
  setWithdrawalAmount,
  setIsCryptoWithdrawalModalOpen,
  setIsWithdrawalError,
  useWithdrawalSlice,
  setDisplayWalletAddress,
} from "src/store/features/withdrawal.slice";

// REDUX
import { useDispatch } from "react-redux";
import {
  WithdrawAddressSeparateRes,
  useGetMinimumWithdrawalAmountMutation,
} from "src/store/features/api/generated";

// TYPES
import { Token } from "./CryptoWithdrawalModal";

// HOOKS
import { useUserSlice } from "src/store/features/user.slice";
import usePermission from "src/utils/hooks/usePermission";
import useVerificationNavigation from "src/components/common/useVerificationNavigation";

// UTILS
import { formatCurrency } from "src/utils/helpers/PriceAndUnitsHelpers";

/**
 * Props for the CryptoWithdrawalInputDetails component.
 *
 * @function
 *
 * @property {Token | null} token - The token to withdraw.
 * @property {string | null} withdrawalAddress - The withdrawal address.
 * @property {string} memo - The memo.
 * @property {function} handleWithdrawalAmountChange - The function to handle the withdrawal amount change.
 * @property {WithdrawAddressSeparateRes[]} cryptoAddresses - The crypto addresses.
 *
 * @returns {JSX.Element} The JSX code to render the StageOneCryptoWithdrawal component.
 */
interface ICryptoWithdrawalInputDetailsProps {
  token: Token | null;
  withdrawalAddress: string | null;
  memo?: string;
  cryptoAddresses?: WithdrawAddressSeparateRes[];
}

export const CryptoWithdrawalInputDetails: React.FC<ICryptoWithdrawalInputDetailsProps> = ({
  token,
  withdrawalAddress,
  memo,
  cryptoAddresses,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { handleVerification } = useVerificationNavigation();
  const [selectTokenMessage, setSelectTokenMessage] = useState<boolean>(false);
  const [withdrawalTokenValue, setWithdrawalTokenValue] = useState<number>(0);
  const [allAvailableTokens, setAllAvailableTokens] = useState<Token[] | null>(null);
  const [addressList, setAddressList] = useState<
    { value: string; formatName: string; memo: string }[]
  >([]);
  const [minimumWithdrawalAmount, setMinimumWithdrawalAmount] = useState<number>(0);
  const [filteredAddresses, setFilteredAddresses] = useState<
    { value: string; formatName: string; memo: string }[]
  >([]);
  const [withdrawalDisplayAmount, setWithdrawalDisplayAmount] = useState<string>("");

  const { userHoldings, is2FAEnabled, defaultCurrency } = useUserSlice();
  const { isWithdrawalError } = useWithdrawalSlice();
  const { userPermission } = usePermission();

  const [
    getMinimumWithdrawalAmount,
    { isLoading: isWithdrawalAmountLoading, isSuccess: isWithdrawalAmountSuccess },
  ] = useGetMinimumWithdrawalAmountMutation();

  useEffect(() => {
    if (withdrawalAddress === "No option available") {
      dispatch(setWalletAddress(""));
      history.push("/wallets/addresses");
    }
  }, [withdrawalAddress]);

  const handleTokenChange = async (e: any) => {
    dispatch(setWalletAddress(""));
    setSelectTokenMessage(false);
    if (allAvailableTokens) {
      const value = allAvailableTokens.find((token) => token.displayName === e.target.value);
      if (value) {
        const token = allAvailableTokens.find((token) => token.displayName === e.target.value);
        if (token) {
          await getMinimumWithdrawalAmount({
            minimumWithdrawalDto: {
              token: token.code,
            },
          })
            .unwrap()
            .then((data) => {
              setMinimumWithdrawalAmount(data);
              const tokenWithdrawalValue = calculateWithdrawalValue(token, data);
              setWithdrawalTokenValue(tokenWithdrawalValue);
              dispatch(setToken(token));
            });
        }
      }
    }
  };

  const handleSelectAddress = (e: any) => {
    dispatch(setDisplayWalletAddress(e.target.displayName));
    dispatch(setWalletAddress(e.target.value));
    if (e.target.memo) {
      dispatch(setMemo(e.target.memo));
    }
  };

  const handleMemoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setMemo(event.target.value));
  };

  const handleWithdrawalAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // to allow for decimal point and only numbers, need to remove all non-numeric characters
    let value = event.target.value;
    value = value.replace(/[^\d.]/g, "");
    // if there are more than one decimal point, return
    if (value.split(".").length > 2) return;

    // the display amount will be a string value that is shown in the input field component
    setWithdrawalDisplayAmount(value);

    // parse the value to a number and set the withdrawal amount in the store
    const newAmount = parseFloat(value);
    if (token && token.available < newAmount) {
      dispatch(setIsWithdrawalError(true));
    } else {
      dispatch(setIsWithdrawalError(false));
    }
    dispatch(setWithdrawalAmount(newAmount));
  };

  const handleNavigateToTrade = () => {
    dispatch(setIsCryptoWithdrawalModalOpen(false));
  };

  const handleNavigateTo2Fa = () => {
    dispatch(setIsCryptoWithdrawalModalOpen(false));
  };

  const handleNavigateToVerify = () => {
    handleVerification();
    dispatch(setIsCryptoWithdrawalModalOpen(false));
  };

  const handleNavigateToDeposit = () => {
    dispatch(setIsCryptoWithdrawalModalOpen(false));
  };

  const calculateWithdrawalValue = (token: Token, withdrawalAmount: number) => {
    if (!token.price) return 0;

    return token.price * withdrawalAmount;
  };

  const incrementMinimumAmount = (amount: number, decimalPlaces: number): string => {
    const amountStr = amount.toFixed(decimalPlaces);
    const [integer, decimals] = amountStr.split(".");
    const decimalDigits = decimals.split("");
    decimalDigits[decimalPlaces - 1] = (parseInt(decimalDigits[decimalPlaces - 1]) + 1).toString();

    const incrementedDecimalPart = decimalDigits.join("");

    return `${integer}.${incrementedDecimalPart}`;
  };

  useEffect(() => {
    if (userHoldings) {
      // Get only crypto holdings
      const cryptoUserHoldings = userHoldings.filter((holding) => {
        return holding.currencyConfig.type === "Crypto";
      });
      // format the data to be used in the dropdown
      const tokens: Token[] = cryptoUserHoldings.map((currencyData: any) => ({
        code: currencyData.currencyConfig.code,
        displayName: currencyData.currencyConfig.displayName,
        available: currencyData.available,
        logoUrl: currencyData.currencyConfig.logoUrl,
        memo: currencyData.currencyConfig.memo,
        format: currencyData.currencyConfig.formatName,
        totalValue: currencyData.totalValue,
        price: currencyData.price,
        value: currencyData.value,
      }));

      setAllAvailableTokens(tokens);
    }
  }, [userHoldings]);

  // Latest selected token code, read when the minimum below resolves so a
  // response for a token the user has since switched away from is dropped.
  const selectedTokenCodeRef = useRef(token?.code);
  selectedTokenCodeRef.current = token?.code;

  // The selected token can arrive without going through `handleTokenChange`:
  // a resumed Travel Rule transfer (`useTravelRuleHydrate`) seeds a stub with
  // `available: 0` and no format/price, and the withdrawal slice persists
  // `selectedToken` to localStorage, so it can also be stale. Swap in the live
  // holding so the balance check, network and minimum reflect reality —
  // otherwise every amount fails with "You have 0 <token> available".
  useEffect(() => {
    if (!token || !allAvailableTokens) return;
    const liveToken = allAvailableTokens.find((t) => t.code === token.code);
    if (!liveToken) return;
    if (liveToken.available === token.available && liveToken.format === token.format) return;

    dispatch(setToken(liveToken));

    const enteredAmount = parseFloat(withdrawalDisplayAmount);
    if (!Number.isNaN(enteredAmount)) {
      dispatch(setIsWithdrawalError(liveToken.available < enteredAmount));
    }

    getMinimumWithdrawalAmount({
      minimumWithdrawalDto: {
        token: liveToken.code,
      },
    })
      .unwrap()
      .then((data) => {
        if (selectedTokenCodeRef.current !== liveToken.code) return;
        setMinimumWithdrawalAmount(data);
        setWithdrawalTokenValue(calculateWithdrawalValue(liveToken, data));
      });
  }, [token?.code, allAvailableTokens]);

  useEffect(() => {
    setFilteredAddresses([{ value: "", formatName: "", memo: "" }]);
    if (cryptoAddresses) {
      const filteredData = cryptoAddresses.filter(
        (item) => item.currencyConfig.displayName === token?.displayName,
      );

      if (filteredData.length > 0) {
        setFilteredAddresses(
          filteredData
            .filter(
              (item) =>
                item.currencyConfig.permissions &&
                item.currencyConfig.permissions.withdraw === true,
            )
            .map((item) => {
              const currencyCode = item.currencyConfig.code;
              const displayName = `${item.nickname ?? `My ${currencyCode} Wallet`} - ${
                item.address
              }`;
              return {
                value: item.address,
                displayName: displayName,
                formatName: item.currencyConfig.formatName,
                memo: item.memo ?? "",
              };
            }),
        );
      }
    }
  }, [token]);

  useEffect(() => {
    if (token && filteredAddresses.length > 0) {
      setAddressList(filteredAddresses);
    }
  }, [filteredAddresses]);

  return (
    <Grid container direction="column">
      {!userHoldings && is2FAEnabled && userPermission !== "guest" && (
        <AlertCard
          id="withdraw-crypto-modal-no-holdings"
          titleText={"Action Required"}
          subText={
            "It looks like you have no holdings available. Make your first trade {{link}} or deposit your holdings {{link}}."
          }
          links={["/trade", "/wallets/deposit"]}
          linkTexts={["here", "here"]}
          callback={handleNavigateToTrade}
        />
      )}

      {!is2FAEnabled && userHoldings && userPermission !== "guest" && (
        <AlertCard
          id="withdraw-crypto-modal-2FA-disabled"
          titleText={"Action Required"}
          subText={"You need to enable 2FA to withdraw. Enable it {{link}}."}
          link={"/settings/security"}
          linkText={"here"}
          callback={handleNavigateTo2Fa}
        />
      )}

      {!userHoldings && !is2FAEnabled && userPermission !== "guest" && (
        <AlertCard
          titleText={"Action Required"}
          subText={
            "It looks like you have no holdings available, and 2FA authentication is disabled. Enable your 2FA {{link}} or deposit your holdings {{link}}."
          }
          links={["/settings/security", "/wallets/deposit"]}
          linkTexts={["here", "here"]}
          callback={handleNavigateToTrade}
        />
      )}

      {userPermission === "guest" && (
        <AlertCard
          id="withdraw-crypto-modal-guest"
          titleText={"Action Required"}
          subText={"You need to verify your account to access this feature. Verify it here."}
          callback={handleNavigateToVerify}
        />
      )}

      <Grid item pb={"28px"}>
        <FormControl error={selectTokenMessage} fullWidth>
          <FormSelect
            title="Select Token"
            type={SelectType.IMAGE}
            labelId="select-token-label"
            id="select-token"
            placeholder="Token"
            value={token?.displayName || ""}
            onChange={handleTokenChange}
            options={
              allAvailableTokens?.map((token) => ({
                value: token.displayName,
                image: token.logoUrl,
              })) || []
            }
          />
          {selectTokenMessage && (
            <Typography pt={1} fontWeight={400} fontSize={14} color="red" fontFamily={"Söhne"}>
              Please select a token to set a withdrawal amount.
            </Typography>
          )}
        </FormControl>
      </Grid>
      <Grid item pb={"28px"}>
        <FormControl fullWidth>
          <FormSelect
            title="Select Address"
            labelId="select-address-label"
            id="select-address"
            placeholder="Token address"
            value={withdrawalAddress || ""}
            onChange={handleSelectAddress}
            options={addressList}
            disabled={!token}
          />
        </FormControl>
      </Grid>
      {token && withdrawalAddress && token.memo && (
        <Grid item pb={"28px"}>
          <FormInput title="Memo" onChange={handleMemoChange} value={memo ?? ""} disabled />
        </Grid>
      )}
      <Grid item pb={"28px"}>
        <FormInput id="network-input" title="Format/Network" value={token?.format || ""} disabled />
      </Grid>
      <Grid item pb={"28px"}>
        <FormInput
          id="amount-input"
          title="Amount to Withdraw"
          value={withdrawalDisplayAmount}
          type={"standard"}
          styleType={isWithdrawalError ? InputStyleType.ERROR : InputStyleType.DEFAULT}
          disabled={!token}
          // InputAdornment={<StyledMaxButton onClick={handleMaxButton}>Add Max</StyledMaxButton>}
          onChange={handleWithdrawalAmountChange}
        />
        {isWithdrawalAmountLoading && (
          <Box py={2}>
            <LinearProgress />
          </Box>
        )}
        {isWithdrawalAmountSuccess && (
          <>
            <Typography pt={1} fontWeight={400} fontSize={14} color="#5C6469" fontFamily={"Söhne"}>
              You have {token?.available.toString()} {token?.code} (
              {token?.value !== undefined && formatCurrency(token.value, defaultCurrency.code)})
              available to withdraw.
            </Typography>
            <Typography fontWeight={400} fontSize={14} color="#5C6469" fontFamily={"Söhne"}>
              Note: You must withdraw a minimum of{" "}
              {!!token && `${incrementMinimumAmount(minimumWithdrawalAmount, 4)} ${token.code}`} (
              {withdrawalTokenValue && formatCurrency(withdrawalTokenValue, defaultCurrency.code)})
              .
            </Typography>
          </>
        )}
        {isWithdrawalError && (
          <StyledError>
            You have {token?.available.toString() ?? "0"} {token?.code ?? "Unknown"} available.
            Please choose a different token, or deposit{" "}
            <StyledLink navigateTo={handleNavigateToDeposit}>here</StyledLink>.
          </StyledError>
        )}
      </Grid>
    </Grid>
  );
};
